import React, { useState } from 'react';
import { Input } from '@/components/ui/input';

/*
 * Phase 4A — reusable contact question types (presentation-agnostic).
 *   mobile_or_email     one input; accepts a valid phone number OR email.
 *   contact_preference  No / Yes (neither pre-selected); "Yes" makes the
 *                       linked contact field required (enforced by the form).
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?\d{7,15}$/;

export const isValidMobileOrEmail = (raw) => {
  const v = String(raw || '').trim();
  if (!v) return false;
  return v.includes('@') ? EMAIL_RE.test(v) : PHONE_RE.test(v.replace(/[\s\-().]/g, ''));
};

export function MobileOrEmail2026({ question, answer, onChange, required }) {
  const [touched, setTouched] = useState(false);
  const value = typeof answer === 'string' ? answer : '';
  const invalid = touched && value.trim() !== '' && !isValidMobileOrEmail(value);
  const base = question.placeholder_text || 'Mobile number or email';
  return (
    <div data-testid={`mobile-or-email-${question.id}`}>
      <Input
        type="text"
        inputMode="email"
        autoComplete="email"
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder={required ? base : `${base} (optional)`}
        className="h-12 rounded-xl text-base"
        aria-invalid={invalid}
        data-testid={`mobile-or-email-input-${question.id}`}
      />
      {invalid && (
        <p className="text-[12px] text-red-600 mt-1.5" role="alert" data-testid={`mobile-or-email-error-${question.id}`}>
          Enter a valid mobile number (7–15 digits) or email address (name@example.com).
        </p>
      )}
    </div>
  );
}

const PREF_OPTIONS = [{ v: 'no', label: 'No' }, { v: 'yes', label: 'Yes' }];

// Answer is 'yes' | 'no', or { choice, name } when the question asks for a Name on Yes.
export const prefChoice = (a) => (typeof a === 'string' ? a : a?.choice);
export const prefName = (a) => (a && typeof a === 'object' ? a.name || '' : '');

export function ContactPreference2026({ question, answer, onChange, primaryColor = '#1A3E6F' }) {
  const s = question.contact_preference_settings || {};
  const askName = !!s.ask_name_on_yes;
  const requireName = askName && s.require_name_on_yes !== false;
  const choice = prefChoice(answer);
  const name = prefName(answer);
  const pick = (v) => onChange(askName ? { choice: v, ...(v === 'yes' ? { name } : {}) } : v);
  return (
    <div className="space-y-2" role="radiogroup" data-testid={`contact-pref-${question.id}`}>
      {PREF_OPTIONS.map((opt) => {
        const selected = choice === opt.v;
        return (
          <button
            type="button"
            key={opt.v}
            role="radio"
            aria-checked={selected}
            onClick={() => pick(opt.v)}
            className="w-full flex items-center gap-3 border rounded-xl px-4 py-3 text-left transition-colors"
            style={selected ? { borderColor: primaryColor, backgroundColor: '#F8FAFC' } : { borderColor: '#E2E8F0' }}
            data-testid={`contact-pref-${opt.v}-${question.id}`}
          >
            <span className="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0" style={{ borderColor: selected ? primaryColor : '#94A3B8' }}>
              {selected && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />}
            </span>
            <span className="text-sm font-semibold text-[#0F172A]">{opt.label}</span>
          </button>
        );
      })}
      {askName && choice === 'yes' && (
        <div className="pt-2" data-testid={`contact-pref-name-${question.id}`}>
          <label className="block text-xs font-semibold text-[#334155] mb-1.5" htmlFor={`contact-pref-name-input-${question.id}`}>
            Name{requireName && <span className="text-red-500"> *</span>}
          </label>
          <Input
            id={`contact-pref-name-input-${question.id}`}
            value={name}
            maxLength={120}
            autoComplete="name"
            onChange={(e) => onChange({ choice: 'yes', name: e.target.value })}
            placeholder={requireName ? 'Your name' : 'Your name (optional)'}
            className="h-12 rounded-xl text-base"
            data-testid={`contact-pref-name-input-${question.id}`}
          />
        </div>
      )}
    </div>
  );
}
